
import { Component, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogProps,
} from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import {
  GenericTableComponent,
  TableConfig,
  ColumnConfig,
  TableCellDirective,
} from 'src/app/shared/components/generic-table';
import {
  AlertReportDetailDto,
  IndicatorCategoryDto,
} from 'src/generated-sources/openapi/alert-service/alert';
import { CpfMaskPipe } from 'src/app/shared/pipes/cpf-mask.pipe';
import { CnpjMaskPipe } from 'src/app/shared/pipes/cnpj-mask.pipe';
import { formatBrl } from 'src/app/helpers/numbers.utils';
import { AlertIndicatorVariation, RelationshipLevel } from '../models/wallet-alert-report.models';
import { getRelationshipLevelSuffix } from '../models/wallet-alert-report.constants';
import { TruncatedTextComponent } from 'src/app/shared/components/truncated-text/truncated-text.component';

export interface PartnerAlertsDialogData {
  cardData: AlertIndicatorVariation;
  partnerAlerts: AlertReportDetailDto[];
  registryDocument: string;
}

export interface ShareholdingModalRow {
  document: string;
  percentage: number | null;
}

export interface PartnerRegistrationRow {
  document: string;
  label: string;
  value: string;
}

interface PartnerModalRow {
  document: string;
  quantity?: string;
  value?: string;
}

@Component({
  selector: 'app-partner-alerts-dialog',
  templateUrl: './partner-alerts-dialog.component.html',
  styleUrl: './partner-alerts-dialog.component.scss',
  imports: [
    ConfirmationDialogComponent,
    GenericTableComponent,
    TableCellDirective,
    TruncatedTextComponent
],
})
export class PartnerAlertsDialogComponent {
  readonly props: ConfirmationDialogProps;
  readonly formattedRegistryDocument: string;
  readonly rows = signal<PartnerModalRow[] | ShareholdingModalRow[] | PartnerRegistrationRow[]>([]);
  readonly isLoading = signal(false);
  readonly tableConfig:
    | TableConfig<PartnerModalRow>
    | TableConfig<ShareholdingModalRow>
    | TableConfig<PartnerRegistrationRow>;

  private readonly cpfPipe = new CpfMaskPipe();
  private readonly cnpjPipe = new CnpjMaskPipe();

  constructor(
    private readonly dialogRef: MatDialogRef<PartnerAlertsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: PartnerAlertsDialogData,
  ) {
    this.props = {
      title:
        data.cardData.indicatorGroup + getRelationshipLevelSuffix(data.cardData.relationshipLevel),
      cancelButton: { hide: true },
    };
    this.formattedRegistryDocument =
      data.registryDocument.length > 11
        ? this.cnpjPipe.transform(data.registryDocument)
        : this.cpfPipe.transform(data.registryDocument);

    if (data.cardData.isPartnerShareholdingData) {
      this.isLoading.set(true);
      this.tableConfig = this.buildShareholdingTableConfig(data.cardData);
      dialogRef.updateSize('500px');
    } else if (data.cardData.isPartnerRegistrationData) {
      this.isLoading.set(true);
      this.tableConfig = this.buildPartnerRegistrationTableConfig(data.cardData);
      dialogRef.updateSize('800px');
    } else {
      this.rows.set(this.groupRows(data.partnerAlerts));
      this.tableConfig = this.buildTableConfig(data.cardData);
      dialogRef.updateSize('600px');
    }
  }

  resolveShareholding(rows: ShareholdingModalRow[] | null): void {
    this.resolveAsyncRows(rows);
  }

  resolveRegistrationData(rows: PartnerRegistrationRow[] | null): void {
    this.resolveAsyncRows(rows);
  }

  private resolveAsyncRows(rows: ShareholdingModalRow[] | PartnerRegistrationRow[] | null): void {
    if (rows === null) {
      this.dialogRef.close();
      return;
    }
    this.rows.set(rows);
    this.isLoading.set(false);
  }

  private groupRows(partnerAlerts: AlertReportDetailDto[]): PartnerModalRow[] {
    const map = new Map<string, PartnerModalRow>();
    for (const a of partnerAlerts) {
      const doc = a.document ?? '';
      if (!map.has(doc)) {
        map.set(doc, { document: doc });
      }
      const row = map.get(doc);
      if (!row) continue;
      if (a.category === IndicatorCategoryDto.Quantity) {
        row.quantity = a.currentValue;
      } else if (a.category === IndicatorCategoryDto.Value) {
        row.value = a.currentValue;
      }
    }
    return Array.from(map.values());
  }

  private buildTableConfig(cardData: AlertIndicatorVariation): TableConfig<PartnerModalRow> {
    const isL1 = cardData.relationshipLevel === RelationshipLevel.L1;
    const isScore = !!cardData.indicatorGroup?.toLowerCase().includes('score');

    const docLabel = isL1
      ? $localize`:@@partnerModalDocL1:Documento del socio`
      : $localize`:@@partnerModalDocL2:Documento de la otra part.`;

    const docColumn: ColumnConfig<PartnerModalRow> = {
      id: 'document',
      title: docLabel,
      textAlign: 'center',
      getValue: (row) => this.formatPartnerDocument(row.document),
    };

    const quantityColumn: ColumnConfig<PartnerModalRow> = {
      id: 'quantity',
      title: $localize`:@@js_quantity:Cantidad`,
      textAlign: 'center',
      getValue: (row) => (row.quantity == null ? '-' : String(Math.trunc(Number(row.quantity)))),
    };

    const valueColumn: ColumnConfig<PartnerModalRow> = {
      id: isScore ? 'quantity' : 'value',
      title: $localize`:@@currentValue:Valor actual`,
      textAlign: 'center',
      getValue: (row) => {
        if (isScore) return row.quantity ?? '-';
        return row.value ? formatBrl(row.value) : '-';
      },
    };

    const columns: ColumnConfig<PartnerModalRow>[] = [docColumn];
    if (!isScore) columns.push(quantityColumn);
    columns.push(valueColumn);

    return { columns };
  }

  private formatPartnerDocument(doc: string): string {
    const clean = doc.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return clean.length === 14 ? this.cnpjPipe.transform(doc) : this.cpfPipe.transform(doc);
  }

  private buildPartnerRegistrationTableConfig(
    cardData: AlertIndicatorVariation,
  ): TableConfig<PartnerRegistrationRow> {
    const isL1 = cardData.relationshipLevel === RelationshipLevel.L1;
    const docLabel = isL1
      ? $localize`:@@partnerModalDocL1:Documento del socio`
      : $localize`:@@partnerModalDocL2:Documento de la otra part.`;

    return {
      columns: [
        {
          id: 'document',
          title: docLabel,
          textAlign: 'center',
          minWidth: '175px',
          maxWidth: '175px',
          getValue: (row) => this.formatPartnerDocument(row.document),
        },
        {
          id: 'label',
          title: $localize`:@@partnerRegistrationFieldLabel:Campo`,
          textAlign: 'left',
          hasCustomContent: true,
          getValue: (row) => row.label || '-',
        },
        {
          id: 'value',
          title: $localize`:@@partnerRegistrationFieldValue:Valor`,
          textAlign: 'left',
          hasCustomContent: true,
          getValue: (row) => row.value || '-',
        },
      ],
    };
  }

  private buildShareholdingTableConfig(
    cardData: AlertIndicatorVariation,
  ): TableConfig<ShareholdingModalRow> {
    const isL1 = cardData.relationshipLevel === RelationshipLevel.L1;
    const docLabel = isL1
      ? $localize`:@@partnerModalDocL1:Documento del socio`
      : $localize`:@@partnerModalDocL2:Documento de la otra part.`;

    const columns: ColumnConfig<ShareholdingModalRow>[] = [
      {
        id: 'document',
        title: docLabel,
        textAlign: 'center',
        getValue: (row) => this.formatPartnerDocument(row.document),
      },
      {
        id: 'percentage',
        title: $localize`:@@partnerModalShareholdingPct:Percentual`,
        textAlign: 'center',
        getValue: (row) => {
          if (row.percentage === null) return '-';
          const pct = row.percentage;
          const formatted = pct % 1 === 0 ? `${pct}` : pct.toFixed(2).replace('.', ',');
          return `${formatted}%`;
        },
      },
    ];
    return { columns };
  }
}
